import React, { useMemo, useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';
import {
  useGetTemplateMessagesQuery,
  useDeleteTemplateMessageMutation,
  usePatchTemplateMessageMutation,
} from '@@/services/templateMessage';
import { Trash2, ArrowLeft, MessageSquarePlus, FileText } from 'lucide-react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
  getSortedRowModel,
  SortingState,
  ColumnFiltersState,
  getFilteredRowModel,
} from '@tanstack/react-table';
import EditableCell from '@@/components/Table/EditableCell';
import { Filter } from '@@/components/Table/Filter';

const columnHelper = createColumnHelper<Alacaza.TemplateMessage>();

const TemplateMessagesPage: React.FC = () => {
  const { data: templateMessages } = useGetTemplateMessagesQuery();

  const [deleteTemplateMessage, { isSuccess: isDeletionSuccess }] =
    useDeleteTemplateMessageMutation();

  const [patchTemplateMessage, { isSuccess: isPatchSuccess }] =
    usePatchTemplateMessageMutation();

  useEffect(() => {
    if (isDeletionSuccess) {
      toast.success('Le message type a bien été supprimé');
    }
  }, [isDeletionSuccess]);

  useEffect(() => {
    if (isPatchSuccess) {
      toast.success('Le message type a bien été modifié');
    }
  }, [isPatchSuccess]);

  const columns = useMemo(
    () => [
      columnHelper.accessor((row) => row, {
        id: 'partner',
        header: 'Partenaire',
        cell: (row) => {
          const { partnerId } = row.getValue();

          return (
            <p className="text-body-sm text-neutral-900 font-medium">
              {partnerId.name}
            </p>
          );
        },
      }),
      columnHelper.accessor('title', {
        id: 'title',
        header: 'Titre',
        cell: EditableCell,
      }),
      columnHelper.accessor('body', {
        id: 'body',
        header: 'Message',
        cell: EditableCell,
        meta: {
          type: 'textarea',
          rows: 2,
          width: 'w-[500px]',
        },
      }),

      columnHelper.accessor((row) => row, {
        id: 'delete',
        header: 'Actions',
        enableColumnFilter: false,
        cell: (row) => {
          const { id } = row.getValue();

          return (
            <div className="flex justify-center">
              <button
                onClick={() => {
                  deleteTemplateMessage(id);
                }}
                className="inline-flex items-center px-3 py-2 bg-danger-600 hover:bg-danger-700 text-white rounded-button shadow-button transition-colors duration-200"
              >
                <Trash2 size={16} className="mr-1.5" />
                <span className="text-body-xs font-medium">Supprimer</span>
              </button>
            </div>
          );
        },
      }),
    ],
    [deleteTemplateMessage],
  );

  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const table = useReactTable({
    data: templateMessages || [],
    columns,
    state: {
      sorting,
      columnFilters,
    },
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    meta: {
      updateData: (id: string, columnId: string, value: string) => {
        const body = {
          [columnId]: value,
        };

        patchTemplateMessage({ id, ...body });
      },
    },
  });

  return (
    <div className="px-page-sm py-6 lg:px-page lg:py-8 w-full">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
          <div className="flex items-center">
            <FileText size={28} className="text-neutral-900" />
            <h1 className="text-heading-lg text-neutral-900 ml-3">Messages types</h1>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
            <Link
              to="/incidents"
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-white hover:bg-neutral-50 text-neutral-700 font-medium rounded-button border border-neutral-200 transition-colors duration-200 shadow-sm"
            >
              <ArrowLeft size={18} />
              <span className="text-body-sm">Retour aux incidents</span>
            </Link>

            <Link
              to="/template-messages/new"
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-button shadow-button transition-colors duration-200"
            >
              <MessageSquarePlus size={18} />
              <span className="text-body-sm">Nouveau message type</span>
            </Link>
          </div>
        </div>

        {/* Mobile Cards - visible < lg */}
        <div className="lg:hidden space-y-4">
          {(templateMessages || []).map((template) => (
            <div
              key={template.id}
              className="bg-white rounded-card shadow-card border border-neutral-200 p-4 hover:shadow-card-hover transition-shadow"
            >
              <div className="mb-3">
                <p className="font-semibold text-neutral-900 text-body mb-2">{template.title}</p>
                <p className="text-neutral-600 text-body-sm line-clamp-2">{template.body}</p>
              </div>

              <div className="flex justify-end">
                <button
                  onClick={() => {
                    deleteTemplateMessage(template.id);
                  }}
                  className="inline-flex items-center px-3 py-2 bg-danger-600 hover:bg-danger-700 text-white rounded-button shadow-button transition-colors duration-200"
                >
                  <Trash2 size={16} className="mr-1.5" />
                  <span className="text-body-xs font-medium">Supprimer</span>
                </button>
              </div>
            </div>
          ))}

          {(!templateMessages || templateMessages.length === 0) && (
            <div className="bg-white rounded-card shadow-card border border-neutral-200 p-8 text-center">
              <p className="text-body text-neutral-500">Aucun message type trouvé</p>
            </div>
          )}
        </div>

        {/* Desktop Table (éditable) - visible >= lg */}
        <div className="hidden lg:block bg-white rounded-card shadow-card border border-neutral-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-neutral-200">
            <p className="text-body-sm font-semibold text-neutral-700">
              {templateMessages?.length || 0} message{(templateMessages?.length || 0) > 1 ? 's' : ''} type{(templateMessages?.length || 0) > 1 ? 's' : ''}
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        className="px-4 py-3 text-left text-body-sm font-semibold text-white bg-neutral-700 border border-neutral-200"
                      >
                        {header.isPlaceholder ? null : (
                          <>
                            <div
                              className={
                                header.column.getCanSort()
                                  ? 'cursor-pointer select-none flex items-center'
                                  : ''
                              }
                              onClick={header.column.getToggleSortingHandler()}
                            >
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext(),
                              )}
                              {{
                                asc: <span className="ml-2 text-xs">↑</span>,
                                desc: <span className="ml-2 text-xs">↓</span>,
                              }[header.column.getIsSorted() as string] ?? null}
                            </div>

                            {header.column.getCanFilter() ? (
                              <Filter column={header.column} />
                            ) : null}
                          </>
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>

              <tbody>
                {table.getRowModel().rows.map((row) => (
                  <tr key={row.id} className="hover:bg-neutral-50 transition-colors">
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className="px-4 py-3 border-b border-neutral-200">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplateMessagesPage;
